/**
 * PlanModal — pick a plan without leaving the step. Renders the SAME
 * PricingPlans component as the billing page (/billing/:botId) inside a
 * modal (portaled, whole-screen backdrop): identical cards, Monthly/Yearly
 * glide, logo rows, hover popovers. Checkout returns to `returnPath` inside
 * the flow, where the page confirms the new plan.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faBolt } from '@fortawesome/free-solid-svg-icons';
import PricingPlans from '../../components/PricingPlans.js';
import { navigateExternal } from '../../utils/navigation.js';
import { API, planName, startCheckout, fetchPlans } from './steps.js';
import './PlanModal.css';

export default function PlanModal({ open, onClose, agent, returnPath, title, subtitle }) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('month');
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    fetchPlans().then((p) => { if (alive) { setPlans(p); setLoading(false); } });
    return () => { alive = false; };
  }, [open]);

  // Same current-state rules as the billing page.
  const sub = agent?.subscription || null;
  const name = planName(agent);
  const currentTier = name !== 'Free' ? name : null;
  const currentInterval = agent?.subscription_plan?.billing_interval || 'month';
  const isInternalSub = sub?.stripe_subscription_id === 'internal';
  const onPaidStripe = name !== 'Free' && !isInternalSub && (!!sub?.stripe_customer_id || !!sub?.stripe_subscription_id);
  const isCancelling = onPaidStripe && agent?.status_display?.className === 'ending';
  const endsText = agent?.status_display?.text || '';

  const choose = useCallback(async (row, key) => {
    setError(''); setPending(key);
    try { await startCheckout({ agent, plan: row, returnPath }); }
    catch (e) { console.error('checkout failed', e); setError(t('billing_checkout_start_error')); setPending(null); }
  }, [agent, returnPath, t]);

  const portal = useCallback(async (key) => {
    if (!sub?.stripe_customer_id && !sub?.stripe_subscription_id) { setError(t('billing_no_stripe_sub')); return; }
    setError(''); setPending(key);
    try {
      const res = await fetch(`${API}create-customer-portal-session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_customer_id: sub.stripe_customer_id || null, stripe_subscription_id: sub.stripe_subscription_id || null }),
      });
      const data = await res.json();
      if (data?.url) navigateExternal(data.url); else { setError(t('billing_portal_open_error')); setPending(null); }
    } catch (e) { console.error('portal failed', e); setError(t('billing_portal_open_error')); setPending(null); }
  }, [sub, t]);

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <div className="ac-plan-modal" role="dialog" aria-modal="true" aria-label={title || t('ac_plan_modal_title')}>
        <button type="button" className="ac-plan-modal-close" onClick={onClose} aria-label={t('close')}><FontAwesomeIcon icon={faXmark} /></button>
        <div className="ac-plan-modal-head">
          <span className="ac-plan-modal-icon"><FontAwesomeIcon icon={faBolt} /></span>
          <div>
            <h3 className="ac-plan-modal-title">{title || t('ac_plan_modal_title')}</h3>
            <p className="ac-plan-modal-sub">{subtitle || t('ac_plan_modal_sub')}</p>
          </div>
        </div>
        {/* .bbp-page carries the rk-* design tokens the pricing cards read. */}
        <div className="bbp-page ac-plan-modal-body">
          {loading && !plans.length ? (
            <div className="ac-loading"><span className="ac-spinner" /></div>
          ) : (
            <PricingPlans
              plans={plans}
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
              currentTier={currentTier}
              currentInterval={currentInterval}
              onPaidStripe={onPaidStripe}
              isCancelling={isCancelling}
              endsText={endsText}
              pending={pending}
              busy={pending !== null}
              onChoose={(row, key) => (onPaidStripe ? portal(key) : choose(row, key))}
              onPortal={portal}
            />
          )}
          {error && <div className="ac-error">{error}</div>}
        </div>
      </div>
    </Modal>
  );
}
